import { stringify } from 'csv-stringify/browser/esm/sync';
import type {
  ImportRowErrorCode,
  ImportRowField,
} from '@/lib/campaign-import/row-validation';

/** A row error as the frontend validation or the backend reports it (the latter adds `utmAlreadyUsed`). */
export interface RowErrorLike {
  field: ImportRowField;
  code: ImportRowErrorCode | 'utmAlreadyUsed';
}

export interface ErrorsCsvRow<E extends RowErrorLike = RowErrorLike> {
  /** Row number in the original file. */
  rowNumber: number;
  /** The original cells, as many as `headers`. */
  cells: string[];
  errors: E[];
}

export interface ErrorsCsvParams<E extends RowErrorLike = RowErrorLike> {
  /** Headers of the original file. */
  headers: string[];
  rows: ErrorsCsvRow<E>[];
  rowNumberHeader: string;
  errorsHeader: string;
  /** Readable text of one error (localized by the caller). */
  describeError: (error: E) => string;
}

const BOM = '﻿';
const ERROR_SEPARATOR = ' | ';

/**
 * Only the invalid rows: the row number, the original cells and one column with the reasons.
 * Same encoding as the template (UTF-8 with BOM and `;`).
 */
export function buildErrorsCsv<E extends RowErrorLike>({
  headers,
  rows,
  rowNumberHeader,
  errorsHeader,
  describeError,
}: ErrorsCsvParams<E>): string {
  const records = rows
    .filter((row) => row.errors.length > 0)
    .map((row) => [
      String(row.rowNumber),
      ...row.cells,
      row.errors.map(describeError).join(ERROR_SEPARATOR),
    ]);

  const csv = stringify(records, {
    delimiter: ';',
    header: true,
    columns: [rowNumberHeader, ...headers, errorsHeader],
  });
  return `${BOM}${csv}`;
}
